import React, { useRef, useState, useEffect } from 'react';
// import PropTypes from 'prop-types';

import Translate from '~/components/Translate/Translate';
import StickyAlert, {
    stickyAlertDefaultDelay
} from '~/components/StickyAlert/StickyAlert';

import StickyAlertContext from './StickyAlertContext';
const TOAST_LIMIT = 5;

const StickyAlertManager = props => {
    const ref = useRef({ counter: 0 }).current;

    const [alerts, setAlerts] = useState([]);

    const getMessageText = msg => {
        if (typeof msg === 'string') return msg;
        if (msg?.props?.message) return msg.props.message; // <Translate message="..." />
        return null; // Complex JSX — can't extract stable text for dedup
    };

    const addStickyAlert = params => {
        if (ref.isMounted) {
            const isError = params.status === 'danger';
            const newAlert = {
                ...params, // status, title, subTitle, message
                id: ++ref.counter,
                delay: stickyAlertDefaultDelay,
                closeAfterDelay: !isError
            };
            setAlerts(prevAlerts => {
                const newMsgText = getMessageText(params.message);
                for (const alert of prevAlerts) {
                    // Duplicate prevention — skip if same message already exists
                    if (
                        newMsgText != null &&
                        getMessageText(alert.message) === newMsgText
                    ) {
                        return prevAlerts;
                    }
                }
                // Prepend new alert so newest appears on top, cap at limit
                const updated = [newAlert, ...prevAlerts];
                return updated.length > TOAST_LIMIT
                    ? updated.slice(0, TOAST_LIMIT)
                    : updated;
            });
        }
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const notify = params => {
        if (params.message) {
            return addStickyAlert(params);
        }
    };

    const notifySuccess = params => {
        return notify({
            ...params,
            status: 'success'
        });
    };

    const notifyError = params => {
        params.message ||= <Translate message="An error occurred!" />;
        return notify({
            ...params,
            status: 'danger'
        });
    };

    const notifyWarn = params => {
        params.message ||= <Translate message="Something went wrong" />;
        return notify({
            ...params,
            status: 'warning'
        });
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    useEffect(() => {
        ref.isMounted = true;
        return () => {
            ref.isMounted = false;
            ref.counter = 0;
        };
    }, []);

    const errorAlerts = [];
    const nonErrorAlerts = [];

    for (const obj of alerts) {
        if (obj) {
            const msg = obj.message;
            const key = `alert_${obj.id}_${msg?.props?.message || msg}`;
            const alertEl = (
                <StickyAlert
                    {...{
                        ...obj,
                        show: true,
                        key: key,
                        onClose: () => {
                            setAlerts(prev =>
                                prev.filter(a => a.id !== obj.id)
                            );
                        }
                    }}
                />
            );
            if (obj.status === 'danger') {
                errorAlerts.push(alertEl);
            } else {
                nonErrorAlerts.push(alertEl);
            }
        }
    }

    return (
        <StickyAlertContext.Provider
            value={{
                addStickyAlert,
                notifySuccess,
                notifyError,
                notifyWarn,
                notify
            }}
        >
            {props.children}
            {0 < errorAlerts.length && (
                <div
                    className="sticky-alerts-container sticky-alerts-errors"
                    aria-live="assertive"
                    aria-atomic="true"
                >
                    {errorAlerts}
                </div>
            )}
            {0 < nonErrorAlerts.length && (
                <div
                    className="sticky-alerts-container"
                    aria-live="polite"
                    aria-atomic="true"
                    id={props.id}
                >
                    {nonErrorAlerts}
                </div>
            )}
        </StickyAlertContext.Provider>
    );
};

// StickyAlertManager.propTypes = { children };

export default StickyAlertManager;
